import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  Tooltip,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { CtyButton, CtyShowToast, checkType } from '@circuitry-ai/doc-ui';
import {
  useGetAgentVersionDetailQuery,
  useRestoreAgentVersionMutation,
  type VoiceAgentVersionDetail,
  type VoiceAgentVersionListItem,
} from '@circuitry-ai/doc-data';
import { computeVersionCompareRows } from './versionUtils';

function formatVersionDate(s: string | undefined, locale: string): string {
  if (!s) return '—';
  try {
    const trimmed = String(s).trim();
    const isoWithoutTz =
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(trimmed) &&
      !/(?:Z|[+-]\d{2}:?\d{2})$/.test(trimmed);
    const dateStr = isoWithoutTz ? `${trimmed}Z` : trimmed;
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return s;
  }
}

function formatVersionFieldValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value || '—';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function isStructuredValue(value: unknown): boolean {
  return value !== null && typeof value === 'object';
}

const VoiceVersionCompareGrid = memo(function VoiceVersionCompareGrid({
  detail,
  t,
  locale,
}: Readonly<{
  detail: VoiceAgentVersionDetail;
  t: TFunction;
  locale: string;
}>) {
  const { beforeRows, afterRows } = useMemo(() => computeVersionCompareRows(detail), [detail]);
  return (
    <div className="max-h-[min(55vh,520px)] overflow-y-auto pr-1">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="rounded-xl border border-warning bg-warning/10 p-4">
          <p className="text-sm font-semibold text-foreground mb-1">
            {detail.version <= 1
              ? t('Before (initial)')
              : t('Before (v{{n}})', { n: detail.version - 1 })}
          </p>
          <p className="text-xs text-default-500 font-medium mb-3">
            {t('Updated at:')} {formatVersionDate(detail.created_at, locale)}
          </p>
          <dl className="space-y-2 text-sm">
            {beforeRows.map(({ key, value }) => (
              <div
                key={key}
                className="rounded-r border-l-4 border-warning pl-3 py-2 bg-warning/10"
              >
                <dt className="font-medium text-foreground truncate">{key}</dt>
                <dd
                  className={`text-foreground break-words mt-0.5 ${
                    isStructuredValue(value) || key.includes('system_prompt') || key.includes('prompt')
                      ? 'whitespace-pre-wrap font-mono text-xs'
                      : ''
                  }`}
                >
                  {formatVersionFieldValue(value)}
                </dd>
              </div>
            ))}
            {beforeRows.length === 0 && (
              <p className="text-default-500 font-medium">{t('No changed keys.')}</p>
            )}
          </dl>
        </div>
        <div className="rounded-xl border border-success bg-success/10 p-4">
          <p className="text-sm font-semibold text-foreground mb-1">
            {t('After (v{{n}})', { n: detail.version })}
          </p>
          <p className="text-xs text-default-500 font-medium mb-1">
            {t('Updated at:')} {formatVersionDate(detail.created_at, locale)}
          </p>
          <p className="text-xs text-default-500 font-medium mb-3">
            {t('Updated by:')}{' '}
            {detail.created_by?.name || detail.created_by?.email || '—'}
          </p>
          <p className="text-xs text-default-600 font-medium mb-3">
            {t('Changed:')} {detail.changed_keys?.join(', ') || '—'}
          </p>
          <dl className="space-y-2 text-sm">
            {afterRows.map(({ key, value }) => (
              <div
                key={key}
                className="rounded-r border-l-4 border-success pl-3 py-2 bg-success/10"
              >
                <dt className="font-medium text-foreground truncate">{key}</dt>
                <dd
                  className={`text-foreground break-words mt-0.5 ${
                    isStructuredValue(value) || key.includes('system_prompt') || key.includes('prompt')
                      ? 'whitespace-pre-wrap font-mono text-xs'
                      : ''
                  }`}
                >
                  {formatVersionFieldValue(value)}
                </dd>
              </div>
            ))}
            {afterRows.length === 0 && (
              <p className="text-default-500 font-medium">{t('No changed keys.')}</p>
            )}
          </dl>
        </div>
      </div>
    </div>
  );
});

export interface VoiceAgentVersionDetailsModalProps {
  assistantId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  versionMeta: VoiceAgentVersionListItem | null;
  onRestored?: () => void;
}

const VoiceAgentVersionDetailsModal: React.FC<VoiceAgentVersionDetailsModalProps> = ({
  assistantId,
  isOpen,
  onOpenChange,
  versionMeta,
  onRestored,
}) => {
  const { t, i18n } = useTranslation();
  const versionKey = versionMeta ? String(versionMeta.version) : '';

  const {
    data: detail,
    isLoading,
    isFetching,
    isError,
  } = useGetAgentVersionDetailQuery(
    { assistantId, version: versionKey },
    { skip: !assistantId || !versionKey || !isOpen }
  );

  const [restoreAgentVersion, { isLoading: restoring }] = useRestoreAgentVersionMutation();

  const handleRestore = useCallback(async () => {
    if (!assistantId || !detail) return;
    try {
      await restoreAgentVersion({ assistantId, version: detail.version }).unwrap();
      CtyShowToast(checkType.success, t('Version {{v}} restored.', { v: detail.version }));
      onRestored?.();
      onOpenChange(false);
    } catch (e) {
      let msg = t('Restore failed');
      if (e && typeof e === 'object' && 'data' in e) {
        msg = String((e as { data?: unknown }).data);
      } else if (e instanceof Error) {
        msg = e.message;
      }
      CtyShowToast(checkType.error, msg);
    }
  }, [assistantId, detail, onOpenChange, onRestored, restoreAgentVersion, t]);

  const showSpinner = isLoading || isFetching || (detail != null && detail.version !== versionMeta?.version);

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      placement="center"
      backdrop="blur"
      scrollBehavior="inside"
      size="5xl"
      classNames={{ base: 'max-w-[min(96vw,1100px)]' }}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <span>{t('Version Details')}</span>
              {versionMeta ? (
                <span className="text-sm font-normal text-default-500">
                  {t('Version {{n}}', { n: versionMeta.version })}
                  {' · '}
                  {formatVersionDate(versionMeta.created_at, i18n.language)}
                </span>
              ) : null}
            </ModalHeader>
            <ModalBody className="gap-4">
              {!versionMeta ? (
                <p className="text-sm text-default-500">{t('No version details available.')}</p>
              ) : showSpinner ? (
                <div className="flex items-center justify-center py-12">
                  <Spinner size="lg" label={t('Loading version details…')} />
                </div>
              ) : isError ? (
                <p className="text-sm text-danger font-medium py-6 text-center">
                  {t('Failed to load version details.')}
                </p>
              ) : detail ? (
                <>
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="text-base font-semibold text-foreground">
                      {t('Compare version {{v}}', { v: versionKey })}
                    </h4>
                    <Tooltip content={t('Make this version the current agent configuration')} placement="top" showArrow>
                      <CtyButton
                        size="sm"
                        color="primary"
                        variant="flat"
                        startContent={!restoring ? <Icon icon="mdi:restore" width={18} height={18} /> : undefined}
                        isDisabled={restoring}
                        isLoading={restoring}
                        onPress={() => void handleRestore()}
                        aria-label={t('Use this version')}
                      >
                        {t('Use this version')}
                      </CtyButton>
                    </Tooltip>
                  </div>
                  <VoiceVersionCompareGrid detail={detail} t={t} locale={i18n.language} />
                </>
              ) : (
                <p className="text-sm text-default-500 font-medium py-6 text-center">
                  {t('No details returned for this version.')}
                </p>
              )}
            </ModalBody>
            <ModalFooter>
              <CtyButton variant="light" onPress={onClose}>
                {t('Close')}
              </CtyButton>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default VoiceAgentVersionDetailsModal;
